import {
  computed, onScopeDispose, ref, toValue, watch, type MaybeRefOrGetter, type WatchSource,
} from 'vue';
import { useLocalStorage } from './useLocalStorage';

export type ColumnWidths = Record<string, number>;

/**
 * The slice of PrimeVue's DataTable instance (primevue@5.0.0) this composable relies on. Column
 * widths are applied through the table's own resize style injection (`addColumnWidthStyles`), which
 * targets header/body cells by position, so saved widths are keyed by field and rebuilt into a
 * positional list for whatever columns are currently rendered.
 */
interface DataTableInstance {
  $el: HTMLElement;
  $attrSelector: string;
  styleElement: HTMLStyleElement | null;
  $refs: {
    table?: HTMLElement;
    bodyRef?: { $el?: HTMLElement };
    frozenBodyRef?: { $el?: HTMLElement };
  };
  columns: unknown[];
  /** The `value` prop: the rows currently handed to the table. */
  value?: unknown[] | null;
  /** The `table-style` prop, re-applied to the <table> on every re-render. */
  tableStyle?: unknown;
  columnResizeMode?: string;
  /** Set while a resizer is being dragged; PrimeVue clears it in its own document mouseup listener. */
  columnResizing?: boolean;
  virtualScrollerDisabled?: boolean;
  columnProp: (column: unknown, prop: string) => unknown;
  addColumnWidthStyles: (widths: number[]) => void;
  destroyStyleElement: () => void;
}

type Options = {
  /** PrimeVue DataTable component instance (template ref). */
  dataTable: MaybeRefOrGetter<object | null | undefined>;
  storageKey: MaybeRefOrGetter<string>;
  layoutSignal: WatchSource;
  /**
   * Multiplier between saved widths and rendered pixels, e.g. a display density. Saved widths are
   * always kept in 1x units: multiplied on apply, divided on measure. Default 1.
   */
  widthScale?: MaybeRefOrGetter<number>;
};

const MAX_APPLY_ATTEMPTS = 5;

export const useColumnWidths = (options: Options) => {
  const { getItem, setItem, removeItem } = useLocalStorage();

  const getStoredWidths = (): ColumnWidths | null => getItem<ColumnWidths>(toValue(options.storageKey));

  /** The widths enforced on the table; null leaves PrimeVue's native resizing alone. */
  const activeWidths = ref<ColumnWidths | null>(getStoredWidths());

  const getTable = () => (toValue(options.dataTable) ?? null) as DataTableInstance | null;

  const getWidthScale = () => toValue(options.widthScale) || 1;

  const getHeaderCells = (table: DataTableInstance): HTMLElement[] => {
    const headerRow = table.$el?.querySelector('thead[data-pc-section="thead"] > tr');

    return headerRow ? Array.from(headerRow.children).filter((cell): cell is HTMLElement => cell.tagName === 'TH') : [];
  };

  /**
   * Field (or columnKey) of every rendered header cell, in display order. Hidden columns render no
   * <th>, so they are skipped. Columns without a key (e.g. a frozen actions column) yield null.
   */
  const getVisibleColumnKeys = (table: DataTableInstance): Array<string | null> =>
    (table.columns ?? [])
      .filter(column => !table.columnProp(column, 'hidden'))
      .map(column => (table.columnProp(column, 'columnKey') ?? table.columnProp(column, 'field') ?? null) as string | null);

  /** Pairs each rendered header cell with its column key; null when the two can't be lined up safely. */
  const getKeyedHeaderCells = (table: DataTableInstance): Array<{ key: string | null; cell: HTMLElement }> | null => {
    const cells = getHeaderCells(table);
    const keys = getVisibleColumnKeys(table);

    if (cells.length === 0 || cells.length !== keys.length) {
      return null;
    }

    return cells.map((cell, index) => ({ key: keys[index], cell }));
  };

  const measureWidths = (): ColumnWidths | null => {
    const table = getTable();
    const keyedCells = table ? getKeyedHeaderCells(table) : null;

    if (!keyedCells) {
      return null;
    }

    const scale = getWidthScale();

    return keyedCells.reduce<ColumnWidths>((widths, { key, cell }) => {
      if (key) {
        widths[key] = Math.round(cell.offsetWidth / scale);
      }

      return widths;
    }, {});
  };

  /** Same selector PrimeVue's `addColumnWidthStyles` scopes its cell rules to. */
  const getTableSelector = (table: DataTableInstance): string =>
    `[data-pc-name="datatable"][${table.$attrSelector}] > [data-pc-section="tablecontainer"] ${table.virtualScrollerDisabled ? '' : '> [data-pc-name="virtualscroller"]'} > table[data-pc-section="table"]`;

  const getVirtualBodies = (table: DataTableInstance): HTMLElement[] => (table.virtualScrollerDisabled ? [] : [
    table.$refs.bodyRef?.$el,
    table.$refs.frozenBodyRef?.$el,
  ]).filter((element): element is HTMLElement => element instanceof HTMLElement);

  /** Whether a `table-style` value (string, object, or an array of either) sets `width` itself. */
  const declaresWidth = (style: unknown): boolean => {
    if (typeof style === 'string') {
      return (/(^|[;\s])width\s*:/).test(style);
    }

    if (Array.isArray(style)) {
      return style.some(declaresWidth);
    }

    return !!style && typeof style === 'object' && 'width' in style;
  };

  /**
   * Drops injected width rules and the inline sizes PrimeVue's expand-mode resize leaves behind
   * (`width` and `minWidth` set to the same value). The table's own `table-style` is left intact: a
   * re-render (e.g. hiding a column) re-applies it, restoring its `min-width` but leaving the resize's
   * `width` behind, so an inline width `table-style` doesn't declare itself is always stale.
   */
  const resetTableLayout = (table: DataTableInstance) => {
    table.destroyStyleElement();

    const tableElement = table.$refs.table;

    if (tableElement?.style.width && !declaresWidth(table.tableStyle)) {
      if (tableElement.style.minWidth === tableElement.style.width) {
        tableElement.style.minWidth = '';
      }

      tableElement.style.width = '';
    }

    getVirtualBodies(table).forEach(element => {
      if (element.style.width === element.style.minWidth) {
        element.style.width = '';
      }

      element.style.minWidth = '';
    });
  };

  /**
   * Applies widths by field: saved widths win, every other rendered column keeps its natural width.
   * Retries on the next frame while the header hasn't caught up with the column list yet (e.g. right
   * after the table mounts).
   */
  const applyWidths = (widths: ColumnWidths, attempt = 1) => {
    const table = getTable();

    if (!table) {
      return;
    }

    resetTableLayout(table);

    const keyedCells = getKeyedHeaderCells(table);

    if (!keyedCells) {
      if (attempt < MAX_APPLY_ATTEMPTS) {
        requestAnimationFrame(() => applyWidths(widths, attempt + 1));
      }

      return;
    }

    const scale = getWidthScale();
    const positionalWidths = keyedCells.map(({ key, cell }) => (key && widths[key] ? Math.round(widths[key] * scale) : cell.offsetWidth));

    table.addColumnWidthStyles(positionalWidths);

    if (table.columnResizeMode === 'expand' && table.styleElement) {
      // Let the table grow to fit the columns. This goes in the injected stylesheet rather than
      // inline because the table re-renders its `table-style` and would wipe an inline min-width.
      const tableWidth = `${positionalWidths.reduce((sum, width) => sum + width, 0)}px`;
      table.styleElement.innerHTML += `\n${getTableSelector(table)} { min-width: ${tableWidth} !important; }`;
      getVirtualBodies(table).forEach(element => element.style.minWidth = tableWidth);
    }
  };

  const clearWidths = () => {
    const table = getTable();

    if (table) {
      resetTableLayout(table);
    }
  };

  const saveWidths = (widths: ColumnWidths) => {
    setItem({ key: toValue(options.storageKey), value: widths });
    activeWidths.value = widths;
  };

  const removeWidths = () => {
    removeItem(toValue(options.storageKey));
    activeWidths.value = null;
    clearWidths();
  };

  /** Saves the rendered widths, merged over the active ones so columns hidden right now keep theirs. */
  const saveMeasuredWidths = () => {
    const measuredWidths = measureWidths();

    if (measuredWidths) {
      saveWidths({ ...(activeWidths.value ?? {}), ...measuredWidths });
    }
  };

  /**
   * Auto-saves when a column resize ends. Listening in the capture phase runs this before PrimeVue's
   * own document mouseup listener, so `columnResizing` still says whether a drag was in progress;
   * measuring then waits a task for PrimeVue to apply the new width.
   */
  const onDocumentMouseUp = () => {
    if (getTable()?.columnResizing === true) {
      setTimeout(saveMeasuredWidths);
    }
  };

  document.addEventListener('mouseup', onDocumentMouseUp, true);
  onScopeDispose(() => document.removeEventListener('mouseup', onDocumentMouseUp, true));

  watch(() => toValue(options.storageKey), () => activeWidths.value = getStoredWidths());

  /**
   * Columns without a saved width are locked at their measured width, and an empty table measures
   * them without any cell content (e.g. a frozen actions column collapses to its padding while rows
   * are still loading). Re-applying once rows exist measures them against real content.
   */
  const hasRows = () => (getTable()?.value?.length ?? 0) > 0;

  /**
   * Callers typically pass `() => [visibleColumns, tableColumns]`, a fresh array on every call.
   * As a raw watch source that array always compares as changed, so any re-evaluation of this
   * watch (e.g. `hasRows` reading the row count on every page) re-applied widths and forced two
   * full-table layouts per page load. A computed returns the same array until its dependencies
   * actually change, so rows arriving alone no longer counts as a layout change.
   */
  const layoutSignal = computed(() => toValue(options.layoutSignal as MaybeRefOrGetter<unknown>));

  watch(
    [getTable, layoutSignal, hasRows, getWidthScale],
    () => {
      if (activeWidths.value) {
        applyWidths(activeWidths.value);
      }
    },
    { immediate: true, flush: 'post' },
  );

  return {
    activeWidths,
    applyWidths,
    clearWidths,
    measureWidths,
    removeWidths,
    saveWidths,
  };
};
